import { apiClient } from "@/lib/apiClient";
import { formatCertificateDate } from "@/features/certificates";
import type {
  ClientCertificate,
  ClientEducationCertificateItemApi,
  ClientEducationCertificatesApiResponse,
  ClientEducationCertificatesQueryParams,
} from "../types/client-certificates.types";

const CLIENT_CERTIFICATES_ENDPOINT = "/api/client/education-certificates";

function mapApiItemToClientCertificate(item: ClientEducationCertificateItemApi): ClientCertificate {
  const educationId = item.education?.id ?? item.educationId;
  const enrollmentId = item.education?.enrollment?.id ?? item.enrollmentId ?? item.id;
  const educationName = item.education?.name ?? item.educationName ?? "";
  const issuedAt = item.issuedAt ?? item.createdAt ?? "";
  const title =
    item.certificateName ?? item.title ?? (educationName ? `${educationName} Başarı Belgesi` : "Sertifika");

  return {
    id: item.certificateId ?? item.enrollmentId ?? item.id ?? "",
    title,
    relatedTraining: educationName,
    issueDate: issuedAt ? formatCertificateDate(issuedAt) : "",
    certificateNo: item.certificateNumber ?? item.certificateNo ?? "",
    imageUrl: item.fileUrl ?? item.imageUrl ?? undefined,
    rawHtml: item.parsedHtml ?? undefined,
    enrollmentId,
    educationId,
  };
}

export const clientCertificatesApi = {
  /**
   * List my education certificates (GET /api/client/education-certificates).
   * Query: page, limit, search, educationId, sortBy, sortOrder
   */
  getMyCertificates: async (
    params: ClientEducationCertificatesQueryParams
  ): Promise<{ items: ClientCertificate[]; count: number }> => {
    const { data } = await apiClient.get<ClientEducationCertificatesApiResponse>(
      CLIENT_CERTIFICATES_ENDPOINT,
      { params }
    );

    const rawItems = Array.isArray(data?.data) ? data.data : [];
    const count = data?.count ?? rawItems.length;

    return {
      items: rawItems.map(mapApiItemToClientCertificate),
      count,
    };
  },

  /**
   * Get certificate HTML for an enrollment (GET /api/client/education-certificates/{enrollmentId}/html).
   */
  getCertificateHtml: async (enrollmentId: string): Promise<string | null> => {
    try {
      const { data } = await apiClient.get<string>(
        `${CLIENT_CERTIFICATES_ENDPOINT}/${enrollmentId}/html`,
        { responseType: "text" as any }
      );
      return typeof data === "string" ? data : null;
    } catch {
      return null;
    }
  },

  /**
   * Download certificate PDF with auth header
   * (GET /api/client/education-certificates/{enrollmentId}/pdf, responseType=blob).
   */
  /**
   * Oturumdaki kullanıcının stajyer profili (GET /api/client/intern-profile/me).
   * Stajyer olmayan kullanıcıda null döner.
   *
   * `skipErrorToast`: profil yoksa backend 404 + "Stajyer profili bulunamadı"
   * döner. Bu bir hata değil, yoklamanın olağan sonucu — stajyer olmayan her
   * kursiyer "Sertifikalarım" sayfasını açtığında toast görüyordu.
   */
  getMyInternCertificateInfo: async (): Promise<{
    id: string;
    /** InternStatus — 1 = TAMAMLANDI, sertifika yalnız o zaman üretilir. */
    isCompleted: boolean;
    startDate?: string | null;
    endDate?: string | null;
  } | null> => {
    try {
      const { data } = await apiClient.get<{
        data?: { id?: string; status?: number; startDate?: string; endDate?: string };
        id?: string;
        status?: number;
        startDate?: string;
        endDate?: string;
      }>("/api/client/intern-profile/me", { skipErrorToast: true });
      const profile = data?.data ?? data;
      if (!profile?.id) return null;
      return {
        id: profile.id,
        isCompleted: profile.status === 1,
        startDate: profile.startDate,
        endDate: profile.endDate,
      };
    } catch {
      return null;
    }
  },

  /**
   * Staj sertifikasını indir
   * (GET /api/client/intern-profile/me/certificate.pdf, responseType=blob).
   */
  downloadMyInternCertificatePdf: async (): Promise<void> => {
    try {
      const response = await apiClient.get<Blob>(
        "/api/client/intern-profile/me/certificate.pdf",
        { responseType: "blob" }
      );
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "staj-sertifikasi.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Hata toast'ı apiClient interceptor'ında gösteriliyor
    }
  },

  downloadCertificatePdf: async (enrollmentId: string): Promise<void> => {
    try {
      const response = await apiClient.get<Blob>(
        `${CLIENT_CERTIFICATES_ENDPOINT}/${enrollmentId}/pdf`,
        { responseType: "blob" }
      );

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "certificate.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Hata toast'ı apiClient interceptor'ında gösteriliyor
    }
  },
};

